import { ProviderContext } from "notistack";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { ISeatStatus } from "../../../../Domain/Types/FloorPlan/SeatStatus.type";
import { generatePlaceVariant } from "../../../../Functions/generateVariant";
import { TFunction } from "i18next";
import { TeamMember } from "../../../../../../features/Booking-Form/typings/team-member";
import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import {
  isTeamWorkplaceSeatAssignee,
  isVisitorWorkplaceSeatAssignee
} from "../../../../../../components/BookingForm/functions/form.functions";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { FloorAvailabilityWarningData } from "../../../../../../features/FloorManager/typings/floor-warningData.entity";
import { UserInformation } from "../../../../../../features/Login/typings/login.types";
import { PlaceVariant } from "../../../../Domain/Types/FloorPlan/PlaceVariant.type";
import { ZoneVariant } from "../../../../Domain/Types/FloorPlan/ZoneVariant.type";
import {
  generateBookingPlaceVariant,
  generateZoneVariant
} from "../../../../Functions/generateVariant";
import { ConnectedBookingData } from "../../../../../../features/Booking-Form/typings/connected-bookingData";

/** True when the place or zone is green/available on the floor plan (not occupied, disabled, forbidden, or filter-temp). */
export function isAvailableForFavoriteAutoSelect(
  { place, zone }: { place: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined },
  seatStatus: ISeatStatus,
  bookingType: BookingType | null
): boolean {
  if (place) {
    const inventoryId = place.inventoryId || place.inventory?.id;
    if (inventoryId == null) {
      return false;
    }
    return (
      generateBookingPlaceVariant(
        { workplace: { ...place, inventoryId }, zone: undefined },
        seatStatus,
        bookingType
      ) === PlaceVariant.AVAILABLE
    );
  }
  if (zone) {
    return generateZoneVariant(zone, seatStatus) === ZoneVariant.AVAILABLE;
  }
  return false;
}

export type SelectableByVariantOptions = {
  allowInactiveResourceBooking?: boolean;
  disabledInventoryIds?: number[];
};

export function selectableByVariant(
  { place, zone }: { place: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined },
  seatStatus: ISeatStatus,
  bookingType: BookingType | null,
  options?: SelectableByVariantOptions
) {
  if (!bookingType) return false;
  if (!place && !zone) return false;

  const variant = generatePlaceVariant(
    { workplace: place, zone: zone },
    seatStatus,
    undefined,
    bookingType
  );

  if (variant === PlaceVariant.AVAILABLE || variant === PlaceVariant.TEMP) {
    return true;
  }

  if (variant === PlaceVariant.WEAK && options?.allowInactiveResourceBooking) {
    const inventoryId =
      place?.inventoryId || place?.inventory?.id || zone?.inventory?.id || zone?.inventoryId;
    return inventoryId != null && (options.disabledInventoryIds?.includes(inventoryId) ?? false);
  }

  return false;
}

export function handleSelection(
  bookingInputs: BookingInputs,
  userIndex: number,
  floorPlan: IFloorPayload,
  selectedEntry: {
    selectedPlace: number | undefined;
    selectedZone: number | undefined;
  },
  stateSelectActions: {
    setSelectedPlace: (p: number | undefined) => void;
    setUserIndex: (i: number) => void;
  },
  onSelectActions: {
    onSingleSelect?: (placeInventoryId: number) => void;
    onTeamSelect?: (index: number, id: number) => void;
    onNextStep: () => void;
  },
  snackActions: {
    enqueueSnackbar: ProviderContext["enqueueSnackbar"];
    t: TFunction;
  }
) {
  const { onSingleSelect, onTeamSelect, onNextStep } = onSelectActions;
  const { setSelectedPlace, setUserIndex } = stateSelectActions;
  const { enqueueSnackbar, t } = snackActions;
  const { mode, usersBookedFor } = bookingInputs;

  if (mode === BookingType.TEAM || mode === BookingType.VISITOR) {
    const isTargetUser = mode === BookingType.VISITOR ? isVisitorWorkplaceSeatAssignee : () => true;

    handleTeamSelection(
      usersBookedFor,
      userIndex,
      floorPlan.places,
      selectedEntry.selectedPlace,
      setUserIndex,
      { onNextStep, onTeamSelect },
      { enqueueSnackbar, t },
      isTargetUser
    );
  } else handleSingleSelection(floorPlan, selectedEntry, setSelectedPlace, onSingleSelect);
}

export function handleSingleSelection(
  floorPlan: IFloorPayload,
  selectedEntry: {
    selectedPlace: number | undefined;
    selectedZone: number | undefined;
  },
  setSelectedPlace: (p: number | undefined) => void,
  onSingleSelect?: (placeInventoryId: number) => void
) {
  const { selectedPlace, selectedZone } = selectedEntry;

  let inventoryId: number | undefined;

  if (selectedPlace) {
    const plInventoryId = floorPlan.places.find(wp => wp.id === selectedPlace)?.inventoryId;
    const plInventory = floorPlan.places.find(wp => wp.id === selectedPlace)?.inventory;
    inventoryId = plInventoryId || plInventory?.id;
  }
  if (selectedZone) {
    const znInventoryId = floorPlan.zones.find(zn => zn.id === selectedZone)?.inventoryId;
    const znInventory = floorPlan.zones.find(zn => zn.id === selectedZone)?.inventory;
    inventoryId = znInventoryId || znInventory?.id;
  }

  if (!inventoryId) return;

  onSingleSelect?.(inventoryId);
  setSelectedPlace(undefined);
}

export function handleTeamSelection(
  usersBookedFor: TeamMember[],
  userIndex: number,
  workplaces: IPlaceSchedule[],
  placeId: number | undefined,
  setUserIndex: (i: number) => void,
  onSelectActions: {
    onTeamSelect?: (index: number, placeInventoryId: number) => void;
    onNextStep: () => void;
  },
  snackActions: {
    enqueueSnackbar: ProviderContext["enqueueSnackbar"];
    t: TFunction;
  },
  isTargetUser: (user: TeamMember) => boolean = () => true
) {
  const { onTeamSelect, onNextStep } = onSelectActions;
  const { enqueueSnackbar, t } = snackActions;

  const checkProps =
    userIndex !== undefined && usersBookedFor !== undefined && setUserIndex !== undefined;

  const selectedPlaceInventoryId = workplaces.find(wp => wp.id === placeId)?.inventoryId;
  const selectedPlaceInventory = workplaces.find(wp => wp.id === placeId)?.inventory;
  const selected = selectedPlaceInventoryId || selectedPlaceInventory?.id;

  // sanity check if place is selected properly
  if (!selected) return;

  if (usersBookedFor?.some(user => user.bookingInventoryId === selected)) {
    enqueueSnackbar(t("_alreadyAssginedToAnotherMember"), { variant: "error" });
  } else {
    if (!checkProps) return;

    onTeamSelect?.(userIndex, selected);

    // check if all team members booked
    const tempUsersBookedFor = [...usersBookedFor];
    tempUsersBookedFor[userIndex] = {
      ...tempUsersBookedFor[userIndex],
      bookingInventoryId: selected
    };

    const userNeedsWorkplaceForSelection = (user: TeamMember) =>
      isTargetUser(user) && isTeamWorkplaceSeatAssignee(user);

    const deskTargetUsers = tempUsersBookedFor.filter(userNeedsWorkplaceForSelection);

    if (deskTargetUsers.every(({ bookingInventoryId }) => !!bookingInventoryId)) {
      onNextStep();
      setUserIndex(0);
      return;
    }

    const unbookedUser = tempUsersBookedFor.findIndex(
      user => userNeedsWorkplaceForSelection(user) && !user.bookingInventoryId
    );
    if (unbookedUser >= 0) {
      setUserIndex(unbookedUser);
    }
  }
}

/**
 * Profile image PlaceIcon can load.
 * `user-photos/…` keys become a bucket URL. A key with no bucket is omitted so the desk uses initials.
 * Any other non-empty photo string is kept (absolute URL or existing relative path).
 */
function resolveOccupancyPhoto(photoUrl: string | null | undefined): string | undefined {
  const trimmed = photoUrl?.trim();
  if (!trimmed || trimmed.length <= 2) {
    return undefined;
  }
  const isStorageKey = trimmed.startsWith("user-photos/") || trimmed.startsWith("/user-photos/");
  if (!isStorageKey) {
    return trimmed;
  }
  const bucketBase = process.env.REACT_APP_BUCKET_URL?.replace(/\/+$/, "");
  if (!bucketBase) {
    return undefined;
  }
  return `${bucketBase}/${trimmed.replace(/^\/+/, "")}`;
}

export function genInitialByBooking({
  booking,
  usersBookedFor,
  userInformation
}: {
  booking: {
    firstName?: string;
    surname?: string;
    isExternal?: boolean;
    isVisitor?: boolean;
    photoUrl?: string | null;
    isUserBookedBy?: boolean;
  };
  usersBookedFor: TeamMember[];
  userInformation: UserInformation | undefined;
}) {
  const { photoUrl, firstName, surname, isExternal, isVisitor, isUserBookedBy } = booking;
  const drawablePhoto = resolveOccupancyPhoto(photoUrl);

  // for team/visitor rows, isUserBookedBy can be missing in local state, so also use isVisitor
  if (isUserBookedBy === false || isVisitor === true) {
    if (drawablePhoto) return drawablePhoto;
    if (firstName && surname) return firstName[0] + surname[0];
  }

  // return photo
  if (drawablePhoto) return drawablePhoto;

  // login photo only for this row, not for other internal teammates
  const loginPhoto =
    usersBookedFor[0] === booking && !isExternal && !isVisitor
      ? resolveOccupancyPhoto(userInformation?.photoUrl)
      : undefined;
  if (loginPhoto) {
    return loginPhoto;
  }

  // return initial
  if (firstName && surname) return firstName[0] + surname[0];

  const isBookedForSomeoneElse =
    isUserBookedBy === false || isVisitor === true || isExternal === true;
  if (isBookedForSomeoneElse) {
    return undefined;
  }

  return genInitialByOccupiedPlace(userInformation);
}

export function genInitialByOccupiedPlace(userInformation: UserInformation | undefined) {
  const loginPhoto = resolveOccupancyPhoto(userInformation?.photoUrl);
  if (loginPhoto) {
    return loginPhoto;
  }

  if (userInformation?.firstName && userInformation.surname) {
    return userInformation.firstName[0].toUpperCase() + userInformation.surname[0].toUpperCase();
  }

  return undefined;
}

/**
 * Confirm (`name === "booking"`) may use wizard `usersBookedFor`.
 * Calendar / device / other display hosts must not, or leftover wizard occupants paint on the wrong desks.
 */
export function usersBookedForForDisplayOccupancy(
  displayViewName: string,
  usersBookedFor: TeamMember[] | undefined
): TeamMember[] | undefined {
  if (displayViewName === "booking") {
    return usersBookedFor;
  }
  return undefined;
}

/** Occupancy photo/initials for DisplayFloorPlanView (confirm, calendar, device). Not booking select. */
export function resolveDisplayOccupancyIcon({
  displayViewName,
  usersBookedFor,
  userInformation,
  inventoryId,
  isUserOccupiedPlace
}: {
  displayViewName: string;
  usersBookedFor: TeamMember[] | undefined;
  userInformation: UserInformation | undefined;
  inventoryId: number | undefined;
  isUserOccupiedPlace: boolean;
}) {
  const occupants = usersBookedForForDisplayOccupancy(displayViewName, usersBookedFor);
  const showLoginOccupant = displayViewName === "booking" && isUserOccupiedPlace;
  return generateInitial("booking", occupants, userInformation, [], inventoryId, showLoginOccupant);
}

function colleagueInitialsStack(colleaguesBooking: ConnectedBookingData[]): string {
  const lead =
    colleaguesBooking[0].firstName[0].toUpperCase() +
    colleaguesBooking[0].lastName[0].toUpperCase();
  if (colleaguesBooking.length > 1) {
    return lead + "+" + (colleaguesBooking.length - 1).toString();
  }
  return lead;
}

/**
 * People already booked on this floor whose photo or initials belong on the desk.
 * Schedule-insight rows stay visible when Find colleagues is closed.
 * The selected search is used only when the floor has not sent connected rows yet.
 */
export function colleaguesShownOnBookingFloor(
  connectedBookingData: ConnectedBookingData[] | undefined,
  selectedColleagueFilter: ConnectedBookingData[]
): ConnectedBookingData[] {
  if (connectedBookingData != null && connectedBookingData.length > 0) {
    return connectedBookingData;
  }
  return selectedColleagueFilter;
}

/**
 * Desk marker for colleagues already booked on this place.
 * One person: their photo when it can be loaded, otherwise initials.
 * Several people: initials plus the extra count, so the shared desk stays readable.
 */
export function genInitialByColleaguesBooking(colleaguesBooking: ConnectedBookingData[]) {
  if (colleaguesBooking.length === 1) {
    const photo = resolveOccupancyPhoto(colleaguesBooking[0].photoUrl);
    if (photo) {
      return photo;
    }
  }
  return colleagueInitialsStack(colleaguesBooking);
}

export function generateInitial(
  name: string,
  usersBookedFor: TeamMember[] | undefined,
  userInformation: UserInformation | undefined,
  selectedColleagueFilter: ConnectedBookingData[],
  placeInventoryId: number | undefined,
  isUserOccupiedPlace?: boolean
) {
  // prohibit to generate other cases renders
  if (name !== "booking") return;
  if (!placeInventoryId) return undefined;
  if (!usersBookedFor) {
    if (isUserOccupiedPlace) return genInitialByOccupiedPlace(userInformation);
    return undefined;
  }

  const booking = usersBookedFor?.find(u => u.bookingInventoryId === placeInventoryId);
  const colleaguesBooking = selectedColleagueFilter.filter(
    d => d.bookingInventoryId === placeInventoryId
  );
  // console.log("booking:", booking);

  // when user had bookings and look for user's booking
  if (booking) return genInitialByBooking({ booking, usersBookedFor, userInformation });
  // colleagues booking
  if (colleaguesBooking.length) return genInitialByColleaguesBooking(colleaguesBooking);
  // when user booked for other and look for their booking
  if (isUserOccupiedPlace && usersBookedFor.some(book => !book.bookingInventoryId)) {
    const firstVisitor = usersBookedFor.find(user => user.isVisitor === true);
    const preferredUser = firstVisitor ?? usersBookedFor[0];

    return genInitialByBooking({
      booking: { ...preferredUser },
      usersBookedFor,
      userInformation
    });
  }
  // is user occupied place
  if (isUserOccupiedPlace) return genInitialByOccupiedPlace(userInformation);

  return undefined;
}

export const compareBookingTypeAndPlaceType = (
  bookingType: BookingType | null | undefined,
  { placeTypeId, zoneTypeId }: { placeTypeId: number | undefined; zoneTypeId: number | undefined }
) => {
  if (!bookingType) return true;
  if (!placeTypeId && !zoneTypeId) return false;

  if (bookingType === BookingType.WORKPLACE && placeTypeId === 1) return true;
  if (bookingType === BookingType.TEAM && placeTypeId === 1) return true;
  if (bookingType === BookingType.PARKINGPLACE && placeTypeId === 2) return true;
  if (bookingType === BookingType.CANTEENPLACE && placeTypeId === 3) return true;
  if (bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE && placeTypeId === 4) return true;

  if (bookingType === BookingType.CONFERENCEZONE && zoneTypeId === 3) return true;

  return false;
};

export function clickableByZoneType(
  bookingType: BookingType | null,
  zoneTypeId: number | undefined
) {
  if (zoneTypeId === 0 || !bookingType) return false;
  return bookingType === BookingType.CONFERENCEZONE && zoneTypeId === 3;
}

export function isZoneClickable({
  bookingType,
  zoneTypeId,
  zoneInventoryId,
  seatStatus,
  allowInactiveResourceBooking = false
}: {
  bookingType: BookingType | null;
  zoneTypeId: number | undefined;
  zoneInventoryId: number;
  seatStatus: ISeatStatus;
  allowInactiveResourceBooking?: boolean;
}) {
  if (zoneTypeId === 0 || !bookingType) return false;
  if (bookingType !== BookingType.CONFERENCEZONE || zoneTypeId !== 3) return false;
  if (seatStatus.disableList.includes(zoneInventoryId) && !allowInactiveResourceBooking) {
    return false;
  }
  if (seatStatus.blockedList?.includes(zoneInventoryId)) {
    return false;
  }

  const inSelectablePool =
    seatStatus.availableList.includes(zoneInventoryId) ||
    seatStatus.tempList?.includes(zoneInventoryId);
  const inOtherBookingState =
    seatStatus.occupiedList.includes(zoneInventoryId) ||
    seatStatus.restrictedList.includes(zoneInventoryId);

  return inSelectablePool || inOtherBookingState;
}

/** When admins may book inactive resources, do not grey-out zones via floor-plan `zone.disabled`. */
export function getZoneContainerDisabledForBooking(
  zoneDisabled: boolean,
  allowInactiveResourceBooking: boolean
): boolean {
  return allowInactiveResourceBooking ? false : zoneDisabled;
}

export function clickableByPlaceType(
  bookingType: BookingType | null,
  placeTypeId: number | undefined
) {
  if (placeTypeId === 0 || !bookingType) return false;
  switch (bookingType) {
    case BookingType.WORKPLACE:
    case BookingType.TEAM:
      return placeTypeId === 1;

    case BookingType.PARKINGPLACE:
      return placeTypeId === 2;

    case BookingType.CANTEENPLACE:
      return placeTypeId === 3;

    case BookingType.ELECTRICCHARGINGSTATIONPLACE:
      return placeTypeId === 4;

    default:
      return false;
  }
}

/** Type matches the booking AND the inventory is not tandem-blocked. */
export function isPlaceClickable(
  bookingType: BookingType | null,
  placeTypeId: number | undefined,
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): boolean {
  if (!clickableByPlaceType(bookingType, placeTypeId)) {
    return false;
  }
  if (inventoryId != null && (seatStatus.blockedList?.includes(inventoryId) ?? false)) {
    return false;
  }
  return true;
}

/**
 * function to check if user clicks selectable place or zone
 * matching with booking type
 */
export function isClickedSelectableEntity(
  { place, zone }: { place: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined },
  bookingType: BookingType | null,
  seatStatus: ISeatStatus,
  allowInactiveResourceBooking = false
) {
  const zoneClickable = isZoneClickable({
    bookingType,
    zoneTypeId: zone?.inventory?.zoneTypeId,
    zoneInventoryId: zone?.inventory?.id as number,
    seatStatus,
    allowInactiveResourceBooking
  });

  const placeClickable = isPlaceClickable(
    bookingType,
    place?.placeTypeId || place?.inventory?.placeTypeId || 0,
    place?.inventoryId ?? place?.inventory?.id,
    seatStatus
  );

  return zoneClickable || placeClickable;
}

export function selectedEntry(
  floorPlan: IFloorPayload,
  selectedPlace: number | undefined,
  selectedZone: number | undefined
) {
  let place: IPlaceSchedule | undefined;
  let zone: IZoneSchedule | undefined;

  if (selectedPlace) place = floorPlan.places.find(w => w.id === selectedPlace);
  if (selectedZone) zone = floorPlan.zones.find(z => z.id === selectedZone);

  return { place, zone };
}

// compare min and maxUsers of conference zone and booking visitors number, then return boolean
export function checkUserNumberBooking(
  bookingType: BookingType | null,
  minUsers: number | null | undefined,
  maxUsers: number | null | undefined,
  zoneAccess: BookingInputs["zoneAccess"],
  warningData: FloorAvailabilityWarningData
) {
  const { requiredUserWarningData, optionalUserWarningData } = warningData;

  // check the booking type
  if (bookingType !== BookingType.CONFERENCEZONE) return false;
  // sanity check
  if (minUsers === undefined || maxUsers === undefined) return false;

  // expected participants of required user
  const amountRequiredUser = requiredUserWarningData?.amountUsersBookedSomewhereElse ?? 0;
  // expected participants of optional user
  const amountOptionalUser = optionalUserWarningData?.amountUsersBookedSomewhereElse ?? 0;
  const selectedVisitorsNumber = zoneAccess ?? [];

  // compare with the number : visitors + 1 (booking owner)
  if (
    minUsers &&
    minUsers > selectedVisitorsNumber.length + 1 - (amountRequiredUser + amountOptionalUser)
  )
    return true;

  // maxUsers null means unlimited
  if (maxUsers === null) return false;

  if (maxUsers < selectedVisitorsNumber.length + 1 - (amountRequiredUser + amountOptionalUser))
    return true;

  return false;
}

/** Place click handler factory (keeps SelectFloorPlanView coverage without Pixi pointer events). */
export function createPlaceSelectedHandler(
  setSelectedPlace: (placeId: number | undefined) => void,
  placeId: number
): () => void {
  return () => {
    setSelectedPlace(placeId);
  };
}
